import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, KanbanSquare, Wallet, Megaphone, Sparkles,
  Settings as SettingsIcon, LogOut, Menu, X, Zap, CalendarDays, Package, FolderOpen, Plane,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/clients", label: "Clients & Prospects", icon: Users, id: "clients" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/tasks", label: "Tasks", icon: KanbanSquare, id: "tasks" },
  { to: "/inventory", label: "Inventory", icon: Package, id: "inventory" },
  { to: "/deliverables", label: "Deliverables", icon: FolderOpen, id: "deliverables" },
  { to: "/travel", label: "Travel", icon: Plane, id: "travel" },
  { to: "/finance", label: "Invoicing & Expenses", icon: Wallet, id: "finance" },
  { to: "/marketing", label: "Marketing", icon: Megaphone, id: "marketing" },
  { to: "/ai-hub", label: "AI Hub", icon: Sparkles, id: "ai-hub" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
];

function Brand() {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
        <Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
      </div>
      <span className="font-display text-lg font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
    </div>
  );
}

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map((item) => (
        <NavLink
          key={item.id}
          to={item.to}
          onClick={onNavigate}
          data-testid={`sidebar-nav-${item.id}`}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            )
          }
        >
          <item.icon className="h-[18px] w-[18px]" strokeWidth={2} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function CreditsCard({ user }) {
  const used = user?.ai_credits_used || 0;
  const limit = user?.ai_credits_limit || 20;
  const pct = Math.min(100, (used / limit) * 100);
  return (
    <div className="rounded-2xl border border-border bg-secondary/50 p-4" data-testid="ai-credits-card">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">AI Credits</span>
        <span className="text-xs font-semibold text-accent">{({ free: "Free", pro: "BizForge Core", business: "BizForge Command" })[user?.plan] || user?.plan}</span>
      </div>
      <Progress value={pct} className="h-1.5" />
      <p className="mt-2 text-xs text-muted-foreground">{used} / {limit} used this cycle</p>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card px-4 py-6 lg:flex">
        <div className="px-2"><Brand /></div>
        <div className="mt-8 flex-1"><NavItems /></div>
        <div className="space-y-4">
          <CreditsCard user={user} />
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-accent/15 text-accent text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-button" className="text-muted-foreground hover:text-destructive">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur-md lg:hidden">
        <Brand />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open">
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-card p-5 shadow-xl animate-fade-up">
            <div className="flex items-center justify-between">
              <Brand />
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="mt-6"><NavItems onNavigate={() => setMobileOpen(false)} /></div>
            <div className="mt-6 space-y-4">
              <CreditsCard user={user} />
              <Button variant="outline" className="w-full" onClick={doLogout} data-testid="mobile-logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar (desktop) */}
      <div className="lg:pl-64">
        <div className="hidden items-center justify-end gap-2 border-b border-border bg-card/60 px-8 py-3 backdrop-blur lg:flex">
          <ThemeToggle />
        </div>
        <main className="px-4 py-6 pb-24 sm:px-6 lg:px-8 lg:py-8 lg:pb-8">{children}</main>
      </div>
    </div>
  );
}
